import {
  CredentialsStore,
  PauseInternetRuleStateStore,
  RoutineRule,
  StoreMethods,
  Stores,
  StoresKeys,
} from '../types/store';
import {
  ActiveSafeNetwork,
  SafeNetworkConfig,
} from '../types/store/safeNetworks';
import { ScreenTime } from '../types/store/screenTime';
import { getChromeLocalStorage } from '../chrome/wrappers';
import { resolvePauseInternetEndsAtInSeconds } from '../helper/pauseInternet';
import { ScreenTimeEvent } from '../types/api/events';
import { Mutex } from 'async-mutex';

const storeMutex = new Mutex();

function makeStore<K extends keyof Stores, Extra>(key: K, extra?: Extra) {
  const storeCommonMethods: StoreMethods<Stores[K]> = {
    set: value => {
      // if the value is an object, merge with the current value
      if (typeof value === 'object' && value !== null) {
        stores[key] = { ...stores[key], ...value };
      } else {
        stores[key] = value;
      }

      return storeMutex.runExclusive(() =>
        getChromeLocalStorage().set({ [key]: stores[key] })
      );
    },
    get: () => stores[key],
    clear: () => {
      delete stores[key];
      return storeMutex.runExclusive(() => getChromeLocalStorage().remove(key));
    },
  };

  return {
    ...storeCommonMethods,
    ...extra,
  } as unknown as StoreMethods<Stores[K]> & Extra; // TS has problems to infer the type of the returned object
}

const rulesStore = makeStore(StoresKeys.RULES, {
  getTimestamp: () => {
    return rulesStore.get()?.timestamp;
  },
  getTimezone: () => {
    return rulesStore.get()?.timezone;
  },
  getTimeRestrictions: () => {
    return rulesStore.get()?.timeRestrictions;
  },
  getPauseInternet: (): PauseInternetRuleStateStore => ({
    endsAtInSeconds: resolvePauseInternetEndsAtInSeconds(
      rulesStore.get()?.timeRestrictions.pause_internet
    ),
  }),
  getDailyQuota: () => rulesStore.get()?.dailyQuota ?? { extraTimeMinutes: 0 },
  getRoutines: (): readonly RoutineRule[] => rulesStore.get()?.routines ?? [],
  getLastSyncedExtensionVersion: () =>
    rulesStore.get()?.lastSyncedExtensionVersion,
  isSafeSearch: () => {
    return rulesStore.get()?.isSafeSearch || false;
  },
  shouldDelegateProtectionInSafeNetwork: () =>
    rulesStore.get()?.safeNetworksRules?.delegateProtectionInSafeNetwork ||
    false,
});
const accountStore = makeStore(StoresKeys.ACCOUNT);
const classificationsStore = makeStore(StoresKeys.CLASSIFICATIONS);
const logsStore = makeStore(StoresKeys.LOGS);
const deviceAndUserStore = makeStore(StoresKeys.DEVICE_AND_USER);

const credentialsStore = makeStore(StoresKeys.CREDENTIALS, {
  refreshTokens: (params: CredentialsStore) =>
    credentialsStore.get() && credentialsStore.set(params),
});

const apiStore = makeStore(StoresKeys.API);

const deviceStore = makeStore(StoresKeys.DEVICE, {
  isSafeNetworkDelegated: () => {
    const delegated = deviceStore.get()?.isProtectionDelegated;
    return !!delegated;
  },
  setIsSafeNetworkDelegated: (delegated: boolean) => {
    deviceStore.set({ isProtectionDelegated: delegated });
  },
  setLastNetworkConnectionType: (lastNetworkConnectionType: ConnectionType) => {
    deviceStore.set({ lastNetworkConnectionType });
  },
  getLastNetworkConnectionType: () =>
    deviceStore.get()?.lastNetworkConnectionType,
});

const safeNetworksStore = makeStore(StoresKeys.SAFE_NETWORKS, {
  setFeatureEnabled: (isEnabled: boolean) => {
    safeNetworksStore.set({ featureEnabled: isEnabled });
  },
  isFeatureEnabled: () => safeNetworksStore.get()?.featureEnabled || false,
  setValidSafeNetworks: (safeNetworks: SafeNetworkConfig[]) => {
    safeNetworksStore.set({ validSafeNetworks: safeNetworks });
  },
  getValidSafeNetworks: () => safeNetworksStore.get()?.validSafeNetworks || [],
  setActiveSafeNetwork: (safeNetwork?: ActiveSafeNetwork) => {
    safeNetworksStore.set({ activeSafeNetwork: safeNetwork });
  },
  getActiveSafeNetwork: () => safeNetworksStore.get()?.activeSafeNetwork,
  setProbeRetries: (retries: number) =>
    safeNetworksStore.set({ probeRetries: retries }),
  getProbeRetries: () => safeNetworksStore.get()?.probeRetries || 0,
  setNextUpdateTimestamp: (nextUpdateTimestamp: number) =>
    safeNetworksStore.set({ nextUpdateTimestamp }),
  getNextUpdateTimestamp: () =>
    safeNetworksStore.get()?.nextUpdateTimestamp || -1,
});

const platformInfoStore = makeStore(StoresKeys.PLATFORM_INFO);

const delegationStore = makeStore(StoresKeys.DELEGATION, {
  // isLoggedIn
  isLoggedIn: () => delegationStore.get()?.isLoggedIn ?? false,
  setIsLoggedIn: (isLoggedIn: boolean) => delegationStore.set({ isLoggedIn }),
  // isDelegationStarted
  isDelegationStarted: () => delegationStore.get()?.isDelegationStarted ?? true,
  setIsDelegationStarted: (isDelegationStarted: boolean) =>
    delegationStore.set({ isDelegationStarted }),
});

const screenTimeStore = makeStore(StoresKeys.SCREEN_TIME, {
  getScreenTime: () =>
    screenTimeStore.get()?.screenTime ?? {
      totalConsumedInMillis: 0,
      slotStartTimestamp: -1,
      elapsedTimeSlotInMillis: 0,
      lastActiveTimestamp: -1,
      lastSyncTimestamp: -1,
    },
  setScreenTime: (screenTime: ScreenTime) =>
    screenTimeStore.set({ screenTime }),
});

const reportingStore = makeStore(StoresKeys.REPORTING, {
  getTimestamp: () => reportingStore.get()?.timestamp,
  setTimestamp: (timestamp: number) => reportingStore.set({ timestamp }),
  getScreenTimeEvents: () => reportingStore.get()?.events?.screenTime ?? [],
  setScreenTimeEvents: (screenTime: ScreenTimeEvent[]) =>
    reportingStore.set({ events: { screenTime } }),
});

const reportedDeviceInfoStore = makeStore(StoresKeys.REPORTED_DEVICE_INFO);

const timeNotificationsStore = makeStore(StoresKeys.TIME_NOTIFICATIONS, {
  isNotifiedDailyQuotaExceeded: () =>
    timeNotificationsStore.get()?.isNotifiedDailyQuotaExceeded ?? false,
  isNotified5MinutesForDailyQuota: () =>
    timeNotificationsStore.get()?.isNotified5MinutesForDailyQuota ?? false,
  isNotified15MinutesForDailyQuota: () =>
    timeNotificationsStore.get()?.isNotified15MinutesForDailyQuota ?? false,

  setIsNotifiedDailyQuotaExceeded: (isNotified: boolean) =>
    timeNotificationsStore.set({ isNotifiedDailyQuotaExceeded: isNotified }),
  setIsNotified5MinutesForDailyQuota: (isNotified: boolean) =>
    timeNotificationsStore.set({ isNotified5MinutesForDailyQuota: isNotified }),
  setIsNotified15MinutesForDailyQuota: (isNotified: boolean) =>
    timeNotificationsStore.set({
      isNotified15MinutesForDailyQuota: isNotified,
    }),

  getLastNotifiedPauseEndsAt: () =>
    timeNotificationsStore.get()?.lastNotifiedPauseEndsAt,
  setLastNotifiedPauseEndsAt: (endsAtInSeconds: number | undefined) =>
    timeNotificationsStore.set({ lastNotifiedPauseEndsAt: endsAtInSeconds }),

  getLastNotifiedRoutineActivation: () =>
    timeNotificationsStore.get()?.lastNotifiedRoutineActivation,
  setLastNotifiedRoutineActivation: (routineActivation: string | undefined) =>
    timeNotificationsStore.set({
      lastNotifiedRoutineActivation: routineActivation,
    }),

  getLastNotifiedRoutinePreStart15Min: () =>
    timeNotificationsStore.get()?.lastNotifiedRoutinePreStart15Min,
  setLastNotifiedRoutinePreStart15Min: (
    routineStartsAtInSeconds: number | undefined
  ) =>
    timeNotificationsStore.set({
      lastNotifiedRoutinePreStart15Min: routineStartsAtInSeconds,
    }),

  getLastNotifiedRoutinePreStart5Min: () =>
    timeNotificationsStore.get()?.lastNotifiedRoutinePreStart5Min,
  setLastNotifiedRoutinePreStart5Min: (
    routineStartsAtInSeconds: number | undefined
  ) =>
    timeNotificationsStore.set({
      lastNotifiedRoutinePreStart5Min: routineStartsAtInSeconds,
    }),
});

let stores: {
  [key in StoresKeys]?: Stores[key];
} = {};

let initPromise: Promise<void> | undefined;
let storesInitialized = false;

export const ensureStoresAreInit = () => {
  if (storesInitialized) return Promise.resolve();
  if (initPromise) return initPromise;
  return initStores();
};

/**
 * Initialise storage cache
 *
 * @remarks
 * Add here any store you create, so it's properly initialised at start.
 * Includes an early return of the current promise avoiding multiple calls to initStores in parallel
 */
export const initStores = () => {
  if (initPromise) return initPromise;
  initPromise = new Promise<void>(resolve => {
    getChromeLocalStorage().get(
      [
        StoresKeys.CREDENTIALS,
        StoresKeys.ACCOUNT,
        StoresKeys.DEVICE_AND_USER,
        StoresKeys.RULES,
        StoresKeys.TIMES,
        StoresKeys.CLASSIFICATIONS,
        StoresKeys.API,
        StoresKeys.DEVICE,
        StoresKeys.SAFE_NETWORKS,
        StoresKeys.PLATFORM_INFO,
        StoresKeys.DELEGATION,
        StoresKeys.SCREEN_TIME,
        StoresKeys.REPORTING,
        StoresKeys.REPORTED_DEVICE_INFO,
        StoresKeys.TIME_NOTIFICATIONS,
        StoresKeys.LOGS,
      ],
      result => {
        stores = result;
        storesInitialized = true;
        resolve();
      }
    );
  }).finally(() => {
    initPromise = undefined;
  });
  return initPromise;
};

export const refreshStores = initStores;

/**
 * Clears the Chrome Local Storage.
 */
export const clearLocalStorage = () => getChromeLocalStorage().clear();

export const getRulesStore = () => rulesStore;
export const getAccountStore = () => accountStore;
export const getDeviceAndUserStore = () => deviceAndUserStore;
export const getCredentialsStore = () => credentialsStore;
export const getClassificationsStore = () => classificationsStore;
export const getLogsStore = () => logsStore;
export const getApiStore = () => apiStore;
export const getDeviceStore = () => deviceStore;
export const getSafeNetworksStore = () => safeNetworksStore;
export const getPlatformInfoStore = () => platformInfoStore;
export const getDelegationStore = () => delegationStore;
export const getScreenTimeStore = () => screenTimeStore;
export const getReportingStore = () => reportingStore;
export const getReportedDeviceInfoStore = () => reportedDeviceInfoStore;
export const getTimeNotificationsStore = () => timeNotificationsStore;
